import React from 'react';
import { Box, Typography } from '@mui/material';

type DosenAlertType = 'default' | 'success' | 'warning' | 'error';
type DosenAlertSize = 'default' | 'small';

interface DosenAlertProps {
  text?: string;
  children?: React.ReactNode;
  type?: DosenAlertType;
  size?: DosenAlertSize;
  showIcon?: boolean;
  icon?: React.ReactNode;
  showLink?: boolean;
  linkName?: string;
  onLinkClick?: () => void;
}

const sizeStyles: Record<DosenAlertSize, { padding: string; gap: number; fontSize: number; lineHeight: string }> = {
  default: { padding: '10px', gap: 20, fontSize: 12, lineHeight: '16px' },
  small: { padding: '8px', gap: 12, fontSize: 11, lineHeight: '14px' }
};

const DosenAlert: React.FC<DosenAlertProps> = ({
  text,
  children,
  type = 'default',
  size = 'default',
  showIcon = true,
  icon,
  showLink = false,
  linkName,
  onLinkClick
}) => {
  const content = children ?? text;
  const styles = sizeStyles[size];

  return (
    <Box
      sx={{
        width: '100%',
        padding: styles.padding,
        background: 'var(--Fill-tooltip-dim, #2A2A2A)',
        borderRadius: '5px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${styles.gap}px`
      }}
      data-type={type}
      data-size={size}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
        {showIcon && icon}

        {content && (
          <Typography
            sx={{
              color: 'var(--Text-text-secondary, #B6B6B6)',
              fontSize: `${styles.fontSize}px`,
              fontWeight: 500,
              fontFamily: 'Inter, sans-serif',
              lineHeight: styles.lineHeight,
              flex: 1
            }}
          >
            {content}
          </Typography>
        )}
      </Box>

      {showLink && linkName && (
        <Box sx={{ padding: '5px', display: 'flex', alignItems: 'center' }}>
          <Box
            component="button"
            type="button"
            onClick={onLinkClick}
            sx={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer'
            }}
          >
            <Typography
              sx={{
                color: 'var(--Text-text-primary, #FDFDFD)',
                fontSize: `${styles.fontSize}px`,
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                textDecoration: 'underline',
                lineHeight: styles.lineHeight
              }}
            >
              {linkName}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DosenAlert;
